import React, { useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { addMonths, subMonths, startOfMinute, endOfMinute } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { updateBreadcrumb } from '#action/breadcrumb';
import { clearShipment } from '#action/shipment/overview';
import { fetchShipments, clearShipments } from '#action/shipment/dashboard';
import { fetchMeta } from '#action/meta/device';
import PreloaderComponent from '#component/preloader';
import { isDataExist, getShipmentsData } from './helper';
import { DesktopView } from './desktop-view/index';
import { MobileView } from './mobile-view/index';
import { setDocumentTitle, getDateRange } from '#helper';

const Dashboard = () => {
  const params = useParams();
  const history = useHistory();
  const { tabNum } = params;
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { shipments, status } = useSelector(s => s.shipments, shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);
  const loading = status === 'loading';

  const dateRange = useMemo(() => {
    const date = new Date();
    const startDate = startOfMinute(subMonths(date, 3));
    const endDate = endOfMinute(addMonths(date, 3));
    return getDateRange(startDate, endDate);
  }, []);

  useEffect(() => {
    setDocumentTitle('Dashboard');
    dispatch(updateBreadcrumb([{ label: 'Dashboard' }]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchMeta());
    dispatch(fetchShipments(dateRange));

    return () => {
      dispatch(clearShipments());
      dispatch(clearShipment());
    };
  }, [dispatch, dateRange]);

  const identifiersMap = {
    100: 'Not started',
    200: 'In progress',
    500: 'Finished'
  };

  const data = useMemo(() => getShipmentsData(shipments), [shipments]);

  const replaceHistoryParams = newParams => {
    const { tabNum: currentTab } = newParams;
    history.replace(`/dashboard/${currentTab}`);
  };

  if (!isDataExist(data) && loading) {
    return <PreloaderComponent />;
  }

  return !isMobile ? (
    <DesktopView
      data={data}
      tabNum={tabNum}
      theme={theme}
      identifiersMap={identifiersMap}
      isMobileBreakpoint={isMobile}
      loading={loading}
      account={account}
    />
  ) : (
    <MobileView
      data={data}
      tab={tabNum}
      identifiersMap={identifiersMap}
      replaceHistoryParams={replaceHistoryParams}
      loading={loading}
      account={account}
    />
  );
};

export default Dashboard;
